import { useState } from 'react';
import { useAuth } from '../context/AuthContext';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Button } from '../components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';
import { 
  Stethoscope, Users, Plus, CalendarRange, Copy, FileText, 
  ChevronDown, Layers
} from 'lucide-react';
import PhysicianSchedule from './schedules/PhysicianSchedule';
import EmployeeSchedule from './schedules/EmployeeSchedule';
import BulkScheduleDialog from '../components/schedules/BulkScheduleDialog';
import WeeklyTemplateDialog from '../components/schedules/WeeklyTemplateDialog';
import CopyDuplicateDialog from '../components/schedules/CopyDuplicateDialog';

export default function Schedules() {
  const { isAdmin } = useAuth();
  const [activeTab, setActiveTab] = useState('physician');
  const [refreshKey, setRefreshKey] = useState(0);
  
  // Dialog states
  const [bulkPhysicianOpen, setBulkPhysicianOpen] = useState(false);
  const [bulkEmployeeOpen, setBulkEmployeeOpen] = useState(false);
  const [templateOpen, setTemplateOpen] = useState(false);
  const [copyDuplicateOpen, setCopyDuplicateOpen] = useState(false);

  const handleRefresh = () => {
    setRefreshKey(prev => prev + 1);
  };

  return (
    <div className="space-y-6" data-testid="schedules-page">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold text-slate-900">Schedules</h1>
          <p className="text-slate-500 mt-1">View and manage staff schedules</p>
        </div>
        
        {/* Admin Quick Actions */}
        {isAdmin && (
          <div className="flex gap-2 flex-wrap">
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button className="bg-teal-600 hover:bg-teal-700" data-testid="quick-actions-btn">
                  <Plus className="h-4 w-4 mr-2" />
                  Quick Actions
                  <ChevronDown className="h-4 w-4 ml-2" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-56">
                <DropdownMenuItem 
                  onClick={() => setBulkPhysicianOpen(true)}
                  data-testid="bulk-physician-action"
                >
                  <Stethoscope className="h-4 w-4 mr-2" />
                  Bulk Physician Schedule
                </DropdownMenuItem>
                <DropdownMenuItem 
                  onClick={() => setBulkEmployeeOpen(true)}
                  data-testid="bulk-employee-action"
                >
                  <Users className="h-4 w-4 mr-2" />
                  Bulk Employee Schedule
                </DropdownMenuItem>
                <DropdownMenuSeparator />
                <DropdownMenuItem 
                  onClick={() => setTemplateOpen(true)}
                  data-testid="template-action"
                >
                  <FileText className="h-4 w-4 mr-2" />
                  Weekly Templates
                </DropdownMenuItem>
                <DropdownMenuSeparator />
                <DropdownMenuItem 
                  onClick={() => setCopyDuplicateOpen(true)}
                  data-testid="copy-duplicate-action"
                >
                  <Copy className="h-4 w-4 mr-2" />
                  Copy / Duplicate
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        )}
      </div>

      <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
        <TabsList className="grid w-full max-w-md grid-cols-2">
          <TabsTrigger value="physician" className="flex items-center gap-2" data-testid="tab-physician">
            <Stethoscope className="h-4 w-4" />
            Physician Schedule
          </TabsTrigger>
          <TabsTrigger value="employee" className="flex items-center gap-2" data-testid="tab-employee">
            <Users className="h-4 w-4" />
            Employee Schedule
          </TabsTrigger>
        </TabsList>

        <TabsContent value="physician" className="mt-6">
          <PhysicianSchedule key={`physician-${refreshKey}`} embedded />
        </TabsContent>

        <TabsContent value="employee" className="mt-6">
          <EmployeeSchedule key={`employee-${refreshKey}`} embedded />
        </TabsContent>
      </Tabs>

      {/* Bulk Physician Dialog */}
      <BulkScheduleDialog
        open={bulkPhysicianOpen}
        onOpenChange={setBulkPhysicianOpen}
        scheduleType="physician"
        onSuccess={handleRefresh}
        currentDate={new Date()}
      />

      {/* Bulk Employee Dialog */}
      <BulkScheduleDialog
        open={bulkEmployeeOpen}
        onOpenChange={setBulkEmployeeOpen}
        scheduleType="employee"
        onSuccess={handleRefresh}
        currentDate={new Date()}
      />

      {/* Weekly Template Dialog */}
      <WeeklyTemplateDialog
        open={templateOpen}
        onOpenChange={setTemplateOpen}
        onSuccess={handleRefresh}
      />

      {/* Copy/Duplicate Dialog */}
      <CopyDuplicateDialog
        open={copyDuplicateOpen}
        onOpenChange={setCopyDuplicateOpen}
        onSuccess={handleRefresh}
        currentDate={new Date()}
      />
    </div>
  );
}
